import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { Button, Input } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import { checkIsValidEmail } from 'utils';
import WarningExclamationIcon from 'icons/WarningExclamation.icon';
import {
  PARSED_SPREADSHEET_RFI_ROW_STATUS,
  ParsedSpreadsheetRFIRow,
} from 'router/subrouters/DataRoom/models/DataRoomRfi';

import './EditRowForm.styles.scss';

type EditRowFormProps = {
  className?: string;
  row: ParsedSpreadsheetRFIRow;
  onSave: (row: ParsedSpreadsheetRFIRow) => void;
  onCancel: () => void;
};

const EditRowForm: React.FC<EditRowFormProps> = (props) => {
  const { className, row, onSave, onCancel } = props;

  const { t } = useTranslation();

  const [document, setDocument] = useState(row.document || '');
  const [assignedTo, setAssignedTo] = useState(row.assignedTo || '');
  const [note, setNote] = useState(row.note || '');

  const isDocumentValid = !!document.trim();
  const isAssignedToValid = checkIsValidEmail(assignedTo);
  const isValid = isDocumentValid && isAssignedToValid;

  const errorMessage = !isDocumentValid
    ? t('documentNameIsMissing')
    : !isAssignedToValid
    ? t('assignedToIsNotValidEmail')
    : '';

  const handleSave = useCallback(() => {
    if (!isValid) return;

    onSave({
      ...row,
      document: document.trim(),
      assignedTo: assignedTo.trim(),
      note: note.trim(),
      status: PARSED_SPREADSHEET_RFI_ROW_STATUS.READY_TO_ADD,
      errors: [],
    });
  }, [isValid, row, document, assignedTo, note, onSave]);

  const classes = classNames('edit-row-form', className);

  return (
    <div className={classes}>
      <div className="edit-row-form__fields">
        <div className="edit-row-form__field">
          <label>{t('documentColumn')}</label>
          <Input
            value={document}
            hasError={!isDocumentValid}
            onChange={(e) => setDocument(e.target.value)}
          />
        </div>

        <div className="edit-row-form__field">
          <label>{t('assignedTo')}</label>
          <Input
            value={assignedTo}
            hasError={!isAssignedToValid}
            onChange={(e) => setAssignedTo(e.target.value)}
          />
        </div>

        <div className="edit-row-form__field">
          <label>{t('note')}</label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      </div>

      <div className="edit-row-form__actions">
        <p className="edit-row-form__error">
          {!!errorMessage && (
            <>
              <WarningExclamationIcon />
              {errorMessage}
            </>
          )}
        </p>

        <div className="edit-row-form__buttons">
          <Button type="button" variant="link" onClick={onCancel}>
            {t('cancel')}
          </Button>
          <Button type="button" disabled={!isValid} onClick={handleSave}>
            {t('save')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditRowForm;
